'use client';

import { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';
import { TRANSLATION_OPTIONS } from '@/lib/constants';

interface ChatInputProps {
  onSend: (message: string, translation?: string) => void;
  disabled?: boolean;
}

export default function ChatInput({ onSend, disabled = false }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [translation, setTranslation] = useState('kjv');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed, translation);
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [message, translation, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 160) + 'px';
  };

  return (
    <div
      className="flex items-end gap-3 p-4 rounded-xl"
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid rgba(200, 164, 94, 0.15)',
      }}
    >
      <select
        value={translation}
        onChange={(e) => setTranslation(e.target.value)}
        className="text-xs px-2 py-1.5 rounded-lg border-none outline-none cursor-pointer shrink-0"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          color: 'var(--text-secondary)',
        }}
      >
        {TRANSLATION_OPTIONS.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>

      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        placeholder="Ask a question about the Bible..."
        disabled={disabled}
        rows={1}
        className="flex-1 bg-transparent text-sm outline-none resize-none placeholder:text-white/30"
        style={{
          color: 'var(--text-primary)',
          maxHeight: '160px',
        }}
      />

      <button
        onClick={handleSubmit}
        disabled={disabled || !message.trim()}
        className="p-2 rounded-lg transition-all duration-200 shrink-0 disabled:opacity-30"
        style={{
          backgroundColor:
            message.trim() && !disabled
              ? 'var(--accent-gold)'
              : 'transparent',
          color:
            message.trim() && !disabled
              ? 'var(--bg-primary)'
              : 'var(--text-secondary)',
        }}
      >
        <Send size={18} />
      </button>
    </div>
  );
}
